import React from 'react';
import { Listbox } from '@headlessui/react';
import { ChevronDownIcon, CheckIcon } from 'lucide-react';
import type { AIAuthorityReportItem } from '../../lib/api-types';

/**
 * Parse a report timestamp into a Date. LARS sends timezone-less ISO strings
 * (e.g. "2026-07-13T10:00:00") which JS would parse as *local* time; those are
 * actually UTC, so append "Z" when there's no timezone marker. The result then
 * renders in the viewer's local time via toLocale*.
 */
export function toLocalDate(iso?: string | null): Date | null {
  if (!iso) return null;
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/i.test(iso.trim());
  const d = new Date(hasTz ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtDate(iso?: string | null): string {
  const d = toLocalDate(iso);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}

function fmtTime(iso?: string | null): string {
  const d = toLocalDate(iso);
  return d ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
}

/** Stable key for a report — prefers lars_report_id (matches latest_report_id). */
export function reportKey(r: AIAuthorityReportItem): string {
  return r.lars_report_id ?? r.full_report_id ?? '';
}

interface ReportSelectorProps {
  reports: AIAuthorityReportItem[];
  current?: AIAuthorityReportItem;
  /** lars_report_id of the latest report, used to tag it. */
  latestId?: string | null;
  onSelect: (report: AIAuthorityReportItem) => void;
  className?: string;
}

const ReportSelector: React.FC<ReportSelectorProps> = ({ reports, current, latestId, onSelect, className = '' }) => {
  const value = current ? reportKey(current) : '';
  const buttonLabel = current?.created_at
    ? `${fmtDate(current.created_at)} · ${fmtTime(current.created_at)}`
    : (current?.last_synced || 'Select report');

  const handleChange = (key: string) => {
    const r = reports.find((x) => reportKey(x) === key);
    if (r) onSelect(r);
  };

  return (
    <Listbox value={value} onChange={handleChange}>
      <div className={`relative ${className}`}>
        <Listbox.Button className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm text-gray-800 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#161463] focus:border-[#161463] transition">
          <span className="font-medium">{buttonLabel}</span>
          <ChevronDownIcon className="h-4 w-4 text-gray-400" />
        </Listbox.Button>
        <Listbox.Options className="absolute right-0 z-30 mt-2 w-72 max-h-80 overflow-auto rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl focus:outline-none">
          {reports.map((r, idx) => {
            const isLatest = !!latestId && r.lars_report_id === latestId;
            return (
              <Listbox.Option
                key={reportKey(r) || idx}
                value={reportKey(r)}
                className={({ active }) => `cursor-pointer rounded-lg px-3 py-2 ${active ? 'bg-gray-50' : ''}`}
              >
                {({ selected }) => (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {r.created_at ? fmtDate(r.created_at) : (r.last_synced || 'Report')}
                        {isLatest && <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-[#161463]">Latest</span>}
                      </span>
                      {r.created_at && <span className="text-xs text-gray-500">{fmtTime(r.created_at)}</span>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full text-xs font-bold bg-[#161463] text-white">
                        {r.overall_grade}
                      </span>
                      {selected && <CheckIcon className="h-4 w-4 text-[#161463]" />}
                    </div>
                  </div>
                )}
              </Listbox.Option>
            );
          })}
        </Listbox.Options>
      </div>
    </Listbox>
  );
};

export default ReportSelector;
